import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  RiArrowLeftLine,
  RiSearchLine,
  RiCodeLine,
  RiTeamLine,
  RiPlayLine,
  RiArrowDownSLine,
  RiCheckLine,
  RiBriefcaseLine,
  RiUserStarLine,
  RiFileTextLine,
  RiMedalLine,
  RiGlobalLine
} from 'react-icons/ri';
import GlassCard from '../../components/ui/v2/GlassCard';
import GlassButton from '../../components/ui/v2/GlassButton';
import CompanySearchInput from '../../components/interview/CompanySearchInput';
import { interviewService } from '../../services/interviewService';

import type {
  InterviewSetup,
  VideoSettings,
  YearsOfExperience,
  InterviewStage,
  Company
} from '../../models/interview';
import { useInterview } from '../../context/InterviewContext';
import { cn } from '../../utils/cn';

interface FormData {
  jobTitle: string;
  jobDescription: string;
  yearsExperience: YearsOfExperience;
  company: string | Company;
  companyCountry: string;
  companyWebsite: string;
  jobPostingUrl: string;
  interviewStage: InterviewStage;
  videoSettings: VideoSettings;
  language: string;


}

export default function InterviewSetupScreen() {
  /* Step state for Wizard flow */
  const [step, setStep] = useState(1);
  const totalSteps = 3;

  /* Form Data State */
  const [formData, setFormData] = useState<FormData>({
    jobTitle: '',
    jobDescription: '',
    yearsExperience: 'SENIOR',
    company: '',
    companyCountry: '',
    companyWebsite: '',
    jobPostingUrl: '',
    interviewStage: 'TECHNICAL_INTERVIEW',
    language: 'en',
    videoSettings: {
      camera_enabled: true,
      recording_mode: 'PRACTICE', // Hidden field, default to PRACTICE as per user request to hide UI
      retake_policy: 'UNLIMITED',
      number_of_questions: 4, // Default per user request
      allow_playback: true,
      show_score_after_upload: true,
      show_score_after_each_question: false
    },

  });

  const [showJobDescription, setShowJobDescription] = useState(true);
  const [isStarting, setIsStarting] = useState(false);
  const [showAdvancedCompany, setShowAdvancedCompany] = useState(true);
  const { setInterviewMode, startSetup, resetSession } = useInterview();
  const navigate = useNavigate();

  // Reset interview session on mount (keeps mode)
  useEffect(() => {
    resetSession();
  }, []);

  const interviewStages = [
    { id: 'INITIAL_SCREENING', label: 'First stage (screening)', icon: RiSearchLine },
    { id: 'TECHNICAL_INTERVIEW', label: 'Technical interview', icon: RiCodeLine },
    { id: 'BEHAVIOURAL_CULTURE', label: 'Behavioral/culture fit', icon: RiTeamLine },
    { id: 'BUSINESS_INTERVIEW', label: 'Business interview', icon: RiBriefcaseLine },
    { id: 'SENIOR_BUSINESS_INTERVIEW', label: 'Senior business interview', icon: RiUserStarLine },
    { id: 'CASE_STUDY_INTERVIEW', label: 'Case study interview', icon: RiFileTextLine },
    { id: 'FINAL_EXECUTIVE_INTERVIEW', label: 'Final executive interview', icon: RiMedalLine }
  ];

  const languages = [
    { code: 'en', label: 'English (US)' },
    { code: 'es', label: 'Spanish' },
    { code: 'fr', label: 'French' },
    { code: 'de', label: 'German' },
    { code: 'it', label: 'Italian' },
    { code: 'pt', label: 'Portuguese' }
  ];

  const handleInputChange = (field: keyof FormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // If selecting a company object, pre-fill advanced fields if available
    if (field === 'company' && typeof value !== 'string') {
      setFormData(prev => ({
        ...prev,
        company: value,
        companyCountry: value.jurisdiction_code || '',
        companyWebsite: value.opencorporates_url || ''
      }));
    }
  };

  const updateVideoSettings = (field: keyof VideoSettings, value: any) => {
    setFormData(prev => ({
      ...prev,
      videoSettings: {
        ...prev.videoSettings,
        [field]: value
      }
    }));
  };

  const nextStep = () => {
    if (step < totalSteps) setStep(step + 1);
  };

  const prevStep = () => {
    if (step > 1) setStep(step - 1);
    else navigate('/interview-mode');
  };

  const companyName = (company: string | Company) =>
    (typeof company === 'string' ? company : company?.name || '').trim();

  const isStepValid = () => {
    // Backend requires setup_context.company.name, so it's required here too
    // despite historically being labeled optional in the UI.
    if (step === 1) return Boolean(formData.jobTitle && (formData.jobDescription || formData.yearsExperience) && companyName(formData.company));
    if (step === 2) return Boolean(formData.interviewStage);
    return true; // Step 3 always valid
  };



  const handleStartInterview = async () => {
    console.log("handleStartInterview called (Video Mode)");
    // Defense in depth: step 1's isStepValid() already blocks reaching this point
    // without a company name, but never send the request without one regardless.
    if (!formData.jobTitle || !companyName(formData.company)) {
      setStep(1);
      return;
    }
    try {
      setIsStarting(true);

      const companyData = typeof formData.company === 'string'
        ? {
          name: formData.company,
          country: formData.companyCountry || undefined,
          website: formData.companyWebsite || undefined,
          job_posting_url: formData.jobPostingUrl || undefined
        }
        : {
          name: formData.company.name,
          country: formData.companyCountry || formData.company.jurisdiction_code || undefined,
          website: formData.companyWebsite || formData.company.opencorporates_url || undefined,
          job_posting_url: formData.jobPostingUrl || undefined
        };

      // VIDEO INTERVIEW FLOW
      const payload: InterviewSetup = {
        setup_context: {
          job_title: formData.jobTitle,
          job_description: formData.jobDescription || undefined,
          years_of_experience: formData.yearsExperience,
          interview_stage: formData.interviewStage,
          company: companyData as any,
          language: formData.language
        },
        video_settings: {
          number_of_questions: formData.videoSettings.number_of_questions,
          language: formData.language,
        } as any
      };

      const response = await interviewService.setupVideoInterview(payload);
      const { interview_id, questions } = response;

      setInterviewMode("video");

      startSetup({
        ...response,
        setup: payload,
        questions: questions as any,
      });

      navigate(`/video-interview/${interview_id}`);
    } catch (error) {
      console.error("Interview setup failed:", error);
      alert("Could not start interview setup. Please try again.");
    } finally {
      setIsStarting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0B0F] text-white selection:bg-white/20 font-sans overflow-hidden relative flex flex-col">

      {/* Background Ambience */}
      <div className="absolute top-0 left-0 w-full h-[500px] bg-gradient-to-b from-purple-900/5 to-transparent pointer-events-none" />

      {/* Header */}
      <header className="relative z-10 p-6 flex items-center justify-between max-w-7xl mx-auto w-full">
        <button
          onClick={prevStep}
          className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors group"
        >
          <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center group-hover:bg-white/10 transition-colors">
            <RiArrowLeftLine />
          </div>
          <span className="text-sm font-medium">Back</span>
        </button>
        <div className="flex gap-2">
          {/* DEV BUTTON */}
          <GlassButton
            variant="danger"
            size="sm"
            onClick={() => {
              setFormData({
                jobTitle: 'Senior Software Engineer',
                jobDescription: 'Leading the development of the new banking platform.',
                yearsExperience: 'SENIOR',
                company: 'Revolut',
                companyCountry: 'GB',
                companyWebsite: 'https://www.revolut.com',
                jobPostingUrl: 'https://www.revolut.com/careers',
                interviewStage: 'TECHNICAL_INTERVIEW',
                language: 'en',
                videoSettings: {
                  camera_enabled: true,
                  recording_mode: 'PRACTICE',
                  retake_policy: 'UNLIMITED',
                  number_of_questions: 4,
                  allow_playback: true,
                  show_score_after_upload: true,
                  show_score_after_each_question: false
                },
              });
              setInterviewMode('video');
              setShowAdvancedCompany(true);
            }}
            className="mr-4 !py-1 !px-3 !text-xs opacity-50 hover:opacity-100"
          >
            Dev Fill
          </GlassButton>

          {[1, 2, 3].map(s => (
            <div key={s} className={cn("h-1 rounded-full transition-all duration-300",
              s === step ? "w-8 bg-white" : s < step ? "w-2 bg-white/40" : "w-2 bg-white/10"
            )} />
          ))}
        </div>
      </header >

      <main className="relative z-10 w-full max-w-2xl mx-auto px-4 flex-1 flex flex-col justify-center pb-20">
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <GlassCard className="p-8 space-y-8">
              {step === 1 && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-2xl font-bold mb-2">Role Details</h2>
                    <p className="text-gray-400 text-sm">Define the position you're interviewing for.</p>
                  </div>

                  {/* Job Title */}
                  <div className="space-y-2">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Job Title *</label>
                    <input
                      type="text"
                      value={formData.jobTitle}
                      onChange={(e) => handleInputChange('jobTitle', e.target.value)}
                      placeholder="e.g., Senior Product Manager"
                      autoFocus
                      className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-gray-600 focus:border-white/30 outline-none transition-colors"
                    />
                  </div>

                  {/* Company Section */}
                  <div className="space-y-2">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Company *</label>
                    <CompanySearchInput
                      value={formData.company}
                      onChange={(val) => handleInputChange('company', val)}
                      placeholder="e.g., TechCorp Inc."
                    />

                    {/* Advanced Company Fields Toggle */}
                    <button
                      onClick={() => setShowAdvancedCompany(!showAdvancedCompany)}
                      className="flex items-center gap-2 text-xs text-gray-500 hover:text-gray-300 transition-colors mt-2"
                    >
                      {showAdvancedCompany ? 'Hide' : 'Add'} details (Website, URL, Country) <RiArrowDownSLine className={cn("transition-transform", showAdvancedCompany ? "rotate-180" : "")} />
                    </button>

                    <AnimatePresence>
                      {showAdvancedCompany && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden space-y-3 pt-2"
                        >
                          <input
                            type="text"
                            value={formData.companyWebsite}
                            onChange={(e) => handleInputChange('companyWebsite', e.target.value)}
                            placeholder="Company Website (e.g. https://google.com)"
                            className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-2 text-sm text-white placeholder:text-gray-600 focus:border-white/30 outline-none"
                          />
                          <input
                            type="text"
                            value={formData.jobPostingUrl}
                            onChange={(e) => handleInputChange('jobPostingUrl', e.target.value)}
                            placeholder="Job Posting URL"
                            className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-2 text-sm text-white placeholder:text-gray-600 focus:border-white/30 outline-none"
                          />
                          <input
                            type="text"
                            value={formData.companyCountry}
                            onChange={(e) => handleInputChange('companyCountry', e.target.value)}
                            placeholder="Country (e.g. US, UK)"
                            className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-2 text-sm text-white placeholder:text-gray-600 focus:border-white/30 outline-none"
                          />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* JD Toggle */}
                  <div className="flex bg-black/20 p-1 rounded-xl border border-white/5">
                    <button onClick={() => setShowJobDescription(true)} className={cn("flex-1 py-2 text-sm font-medium rounded-lg transition-all", showJobDescription ? "bg-white/10 text-white shadow-sm" : "text-gray-500 hover:text-gray-300")}>Job Description</button>
                    <button onClick={() => setShowJobDescription(false)} className={cn("flex-1 py-2 text-sm font-medium rounded-lg transition-all", !showJobDescription ? "bg-white/10 text-white shadow-sm" : "text-gray-500 hover:text-gray-300")}>Experience Level</button>
                  </div>

                  {showJobDescription ? (
                    <textarea
                      value={formData.jobDescription}
                      onChange={(e) => handleInputChange('jobDescription', e.target.value)}
                      placeholder="Paste job description..."
                      rows={5}
                      className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-gray-600 focus:border-white/30 outline-none transition-colors resize-none"
                    />
                  ) : (
                    <div className="relative">
                      <select
                        value={formData.yearsExperience}
                        onChange={(e) => handleInputChange('yearsExperience', e.target.value)}
                        className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-3 text-white appearance-none outline-none focus:border-white/30 transition-colors"
                      >
                        <option value="ENTRY">0-1 years (Entry)</option>
                        <option value="JUNIOR">2-3 years (Junior)</option>
                        <option value="MID">4-6 years (Mid-Level)</option>
                        <option value="SENIOR">7-10+ years (Senior)</option>
                      </select>
                      <RiArrowDownSLine className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    </div>
                  )}
                </div>
              )}

              {step === 2 && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-2xl font-bold mb-2">Interview Stage</h2>
                    <p className="text-gray-400 text-sm">Select the type of interview simulation.</p>
                  </div>
                  <div className="grid gap-3 max-h-[320px] overflow-y-auto custom-scrollbar pr-2">
                    {interviewStages.map((stage) => {
                      const isActive = formData.interviewStage === stage.id;
                      const Icon = stage.icon;
                      return (
                        <button
                          key={stage.id}
                          onClick={() => handleInputChange('interviewStage', stage.id)}
                          className={cn(
                            "flex items-center p-4 border rounded-xl text-left transition-all group",
                            isActive ? "border-white/40 bg-white/10 text-white" : "border-white/5 bg-black/20 text-gray-400 hover:border-white/10 hover:bg-white/5 hover:text-gray-200"
                          )}
                        >
                          <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center mr-4 transition-colors", isActive ? "bg-white text-black" : "bg-white/5 text-gray-500 group-hover:text-gray-300")}>
                            <Icon size={20} />
                          </div>
                          <span className="font-medium">{stage.label}</span>
                          {isActive && <RiCheckLine className="ml-auto" />}
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-2xl font-bold mb-2">Interview Settings</h2>
                    <p className="text-gray-400 text-sm">Configure your video interview.</p>
                  </div>

                  {/* Language Selection */}
                  <div className="space-y-2">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Language</label>
                    <div className="relative">
                      <select
                        value={formData.language}
                        onChange={(e) => handleInputChange('language', e.target.value)}
                        className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-3 pl-10 text-white appearance-none outline-none focus:border-white/30 transition-colors"
                      >
                        {languages.map(lang => (
                          <option key={lang.code} value={lang.code} className="text-black">{lang.label}</option>
                        ))}
                      </select>
                      <RiGlobalLine className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                      <RiArrowDownSLine className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    </div>
                  </div>


                  {/* Questions Count */}
                  <div className="space-y-4 pt-4 border-t border-white/5">
                    <div className="flex justify-between items-center">
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Number of Questions</label>
                      <span className="text-xl font-bold">{formData.videoSettings.number_of_questions}</span>
                    </div>
                    <input
                      type="range"
                      min="1"
                      max="10"
                      value={formData.videoSettings.number_of_questions}
                      onChange={(e) => updateVideoSettings('number_of_questions', parseInt(e.target.value))}
                      className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-white"
                    />
                    <div className="flex justify-between text-[10px] text-gray-500 px-1">
                      <span>1</span>
                      <span>5</span>
                      <span>10</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="pt-6 border-t border-white/5 flex gap-4">
                {step === 3 ? (
                  <GlassButton
                    onClick={handleStartInterview}
                    loading={isStarting}
                    className="w-full py-4 text-base font-semibold !bg-white !text-black hover:!bg-gray-200 !border-none shadow-[0_0_20px_rgba(255,255,255,0.15)]"
                  >
                    Start Interview <RiPlayLine className="ml-2" />
                  </GlassButton>
                ) : (
                  <GlassButton
                    onClick={nextStep}
                    disabled={!isStepValid()}
                    className="w-full py-4 text-base font-semibold"
                  >
                    Next Step <RiArrowLeftLine className="ml-2 rotate-180" />
                  </GlassButton>
                )}
              </div>

            </GlassCard>
          </motion.div>
        </AnimatePresence>
      </main>
    </div >
  );
}
